'use client'

import EllipsisIcon from '@/app/icons/ellipsis'
import { cn } from '@/util/cn'
import { Video, VolumeOffIcon, VolumeOnIcon, type GifOverlayProps } from '@giphy/react-components'
import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent } from 'react'
import { useInView } from 'react-intersection-observer'
import useWindowSize from 'react-use/lib/useWindowSize'
import { EmbedGif, getCaptionTracks } from '../helpers'

type Props = {
    gifs: EmbedGif[]
    showAttribution?: boolean
    title?: string
    username?: string
}

const muteAfterLoops = 3

export default function EmbedVideo({ gifs, showAttribution = false, title, username }: Props) {
    const [index, setIndex] = useState(0)
    const [userMuted, setUserMuted] = useState<boolean>()
    const [menuOpen, setMenuOpen] = useState(false)
    const [captionsEnabled, setCaptionsEnabled] = useState(false)
    const videoRef = useRef<HTMLVideoElement | null>(null)
    const lastLoopCountRef = useRef(0)
    const loopBaselineRef = useRef(0)
    const gif = gifs[index] || gifs[0]
    const sdkGif = useMemo(() => (gif ? { ...gif, analytics_response_payload: '' } : undefined), [gif])
    const tracks = useMemo(() => (sdkGif ? getCaptionTracks(sdkGif) : []), [sdkGif])
    const titleText = title ? `${title}${username ? ` mixtape by ${username}` : ''}` : sdkGif?.title || ''
    const { width: viewportWidth, height: viewportHeight } = useWindowSize()
    const width = Number.isFinite(viewportWidth) ? viewportWidth : 480
    const height = Number.isFinite(viewportHeight) ? viewportHeight : 270
    const [containerRef, inView] = useInView()
    const hasCaptions = tracks.length > 0
    const gifUrl = sdkGif?.url || '#'

    const resetMuteLoop = () => {
        loopBaselineRef.current = lastLoopCountRef.current
    }

    useEffect(() => {
        lastLoopCountRef.current = 0
        loopBaselineRef.current = 0
    }, [sdkGif?.id])

    useEffect(() => {
        const onMessage = ({ data }: MessageEvent) => {
            if (!data || typeof data !== 'object') {
                return
            }

            if (data.action === 'mute') {
                setUserMuted(true)
            } else if (data.action === 'unmute') {
                resetMuteLoop()
                setUserMuted(false)
            } else if (data.action === 'play') {
                videoRef.current?.play()
            } else if (data.action === 'pause') {
                videoRef.current?.pause()
            } else if (data.action === 'stop') {
                videoRef.current?.pause()
                if (videoRef.current) {
                    videoRef.current.currentTime = 0
                }
            } else if (data.action === 'seekTo' && typeof data.value === 'number' && videoRef.current) {
                videoRef.current.currentTime = data.value
            }
        }

        window.addEventListener('message', onMessage)
        return () => window.removeEventListener('message', onMessage)
    }, [])

    const stopOverlayClick = (event: MouseEvent<HTMLElement>) => {
        event.preventDefault()
        event.stopPropagation()
    }

    const Overlay = useCallback(
        ({ isHovered }: GifOverlayProps) => (
            <>
                <button
                    type="button"
                    className={cn(
                        'absolute top-3 right-3 z-20 flex size-10 cursor-pointer items-center justify-center bg-black/70 text-giphyWhite transition hover:bg-giphyCharcoal',
                        isHovered ? 'opacity-100' : 'opacity-0'
                    )}
                    onClick={(event) => {
                        stopOverlayClick(event)
                        setUserMuted((value) => {
                            const nextMuted = !value
                            if (!nextMuted) {
                                resetMuteLoop()
                            }
                            return nextMuted
                        })
                    }}
                    aria-label={userMuted ? 'Unmute video' : 'Mute video'}
                >
                    {userMuted ? <VolumeOffIcon /> : <VolumeOnIcon />}
                </button>

                <div
                    className={cn(
                        'absolute right-4 bottom-4 z-20 transition',
                        isHovered || menuOpen ? 'opacity-100' : 'opacity-0'
                    )}
                    onClick={stopOverlayClick}
                >
                    <button
                        type="button"
                        className="flex size-10 cursor-pointer items-center justify-center bg-black/70 text-giphyWhite transition hover:bg-giphyCharcoal"
                        onClick={(event) => {
                            stopOverlayClick(event)
                            setMenuOpen((value) => !value)
                        }}
                        aria-label="Open video menu"
                    >
                        <EllipsisIcon className="h-5 w-2 rotate-90" />
                    </button>
                    {menuOpen && (
                        <div className="absolute right-12 bottom-0 min-w-max bg-giphyBlack py-2 text-sm shadow-2xl">
                            {hasCaptions && (
                                <button
                                    type="button"
                                    className={cn(
                                        'block w-full cursor-pointer px-4 py-2 text-left transition hover:bg-giphyCharcoal',
                                        captionsEnabled && 'text-giphyBlue'
                                    )}
                                    onClick={(event) => {
                                        stopOverlayClick(event)
                                        setCaptionsEnabled((value) => !value)
                                    }}
                                >
                                    {captionsEnabled ? 'Hide' : 'Show'} Captions
                                </button>
                            )}
                            <a
                                href={gifUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="block px-4 py-2 text-giphyWhite no-underline transition hover:bg-giphyCharcoal"
                            >
                                Watch on GIPHY
                            </a>
                            <span className="absolute right-[-8px] bottom-3 size-0 border-y-8 border-l-8 border-y-transparent border-l-giphyBlack" />
                        </div>
                    )}
                </div>
            </>
        ),
        [captionsEnabled, gifUrl, hasCaptions, menuOpen, userMuted]
    )

    if (!sdkGif) {
        return null
    }

    return (
        <div
            ref={containerRef}
            className="relative h-dvh w-full overflow-hidden bg-black text-giphyWhite"
            onMouseLeave={() => setMenuOpen(false)}
        >
            <Video
                key={sdkGif.id as string}
                gif={sdkGif}
                width={width}
                height={height}
                style={{ width: '100%', height: '100dvh' }}
                controls
                hideAttribution={!showAttribution}
                hideMute
                hideTitle
                ccEnabled={captionsEnabled}
                muted={inView ? userMuted : true}
                loop={gifs.length <= 1}
                overlay={Overlay}
                setVideoEl={(video) => {
                    videoRef.current = video
                    video.title = titleText
                }}
                onMuted={(browserMuted) => {
                    setUserMuted(browserMuted)
                }}
                onUserMuted={(muted) => {
                    if (!muted) {
                        resetMuteLoop()
                    }
                    setUserMuted(muted)
                }}
                onLoop={(count) => {
                    lastLoopCountRef.current = count
                    if (gifs.length <= 1 && count - loopBaselineRef.current >= muteAfterLoops) {
                        setUserMuted(true)
                    }
                }}
                onEnded={() => {
                    if (gifs.length > 1) {
                        setIndex((value) => (value + 1) % gifs.length)
                    }
                }}
            />
        </div>
    )
}
